import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Eye, ImagePlus, Lock, LogIn, LogOut, Save, Sparkles } from "lucide-react";
import { ASSETS } from "../lib/site";
import { createSlug, getStoredArticles, saveArticle } from "../data/articles";

const ADMIN_SESSION_KEY = "sloto69_admin_session";
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "sloto69admin2025";
const GENERATOR_VERSION = "generator-v2";

const today = () => new Date().toISOString().slice(0, 10);

const emptyArticle = {
  title: "",
  slug: "",
  excerpt: "",
  category: "Informasi",
  readTime: "5 menit",
  publishedAt: today(),
  keywords: "",
  imageUrl: "",
  body: "<p>Tulis isi artikel di sini.</p>",
};

export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => window.localStorage.getItem(ADMIN_SESSION_KEY) === "true"
  );
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [article, setArticle] = useState(emptyArticle);
  const [generationDescription, setGenerationDescription] = useState("");
  const [generationError, setGenerationError] = useState("");
  const [generationNotice, setGenerationNotice] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [savedArticle, setSavedArticle] = useState(null);
  const [storedArticles, setStoredArticles] = useState(() => getStoredArticles());

  const generatedSlug = useMemo(
    () => createSlug(article.slug || article.title),
    [article.slug, article.title]
  );

  useEffect(() => {
    document.title = "Admin Artikel | Sloto69";
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    return () => {
      robots.setAttribute("content", "index, follow, max-image-preview:large");
    };
  }, []);

  const handleLogin = (event) => {
    event.preventDefault();

    if (password !== ADMIN_PASSWORD) {
      setLoginError("Password admin tidak sesuai.");
      return;
    }

    window.localStorage.setItem(ADMIN_SESSION_KEY, "true");
    setIsLoggedIn(true);
    setPassword("");
    setLoginError("");
  };

  const handleLogout = () => {
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    setIsLoggedIn(false);
    setSavedArticle(null);
  };

  const updateArticle = (field, value) => {
    setArticle((current) => ({
      ...current,
      [field]: value,
      ...(field === "title" && !current.slug ? { slug: createSlug(value) } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveError("");
    setSaveNotice("");
    setIsSaving(true);

    const saved = saveArticle({
      ...article,
      slug: generatedSlug,
    });

    try {
      const { data } = await axios.post(
        "/api/save-article",
        { article: saved },
        { headers: { "Content-Type": "application/json" } }
      );

      setSavedArticle(data.article || saved);
      setStoredArticles(getStoredArticles());
      setSaveNotice(data.message || "Artikel berhasil disimpan permanen.");
      setArticle({
        ...emptyArticle,
        publishedAt: today(),
      });
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Gagal menyimpan artikel permanen.";

      setSavedArticle(saved);
      setStoredArticles(getStoredArticles());
      setSaveError(
        `${message} Artikel tetap tersimpan sementara di browser ini, tetapi belum permanen.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateArticle = async () => {
    setGenerationError("");
    setGenerationNotice("");

    if (generationDescription.trim().length < 12) {
      setGenerationError("Tulis deskripsi artikel minimal 12 karakter.");
      return;
    }

    setIsGenerating(true);

    try {
      const { data } = await axios.post(
        "/api/generate-article",
        {
          description: generationDescription,
          requestId: `${GENERATOR_VERSION}-${Date.now()}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
        }
      );

      setArticle((current) => ({
        ...current,
        title: data.title || current.title,
        slug: createSlug(data.title || current.title),
        excerpt: data.excerpt || current.excerpt,
        category: data.category || current.category,
        readTime: data.readTime || current.readTime,
        publishedAt: today(),
        keywords: data.keywords || current.keywords,
        imageUrl: data.imageUrl || current.imageUrl,
        body: data.body || current.body,
      }));
      if (data.imageError) {
        setGenerationNotice(
          "Artikel berhasil dibuat, tetapi gambar OpenAI gagal dibuat. Anda bisa isi URL gambar manual atau verifikasi organisasi OpenAI terlebih dahulu."
        );
      }
      setSavedArticle(null);
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Gagal generate artikel.";
      setGenerationError(`[${GENERATOR_VERSION}] ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <main className="min-h-screen aurora-bg flex items-center justify-center px-5 py-10 text-slate-100">
        <section className="w-full max-w-md rounded-lg border border-white/10 bg-[#0F172A]/80 p-6 shadow-2xl">
          <div className="flex items-center justify-between gap-4">
            <img src={ASSETS.logo} alt="Sloto69" className="h-12 w-auto max-w-[180px] object-contain" />
            <Lock className="h-5 w-5 text-[#10B981]" />
          </div>

          <h1 className="mt-8 font-display text-2xl font-semibold">Admin Login</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">
            Masuk untuk menambahkan artikel dari halaman admin.
          </p>

          <form onSubmit={handleLogin} className="mt-6 grid gap-4">
            <label className="grid gap-2 text-sm font-medium text-slate-200">
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                autoComplete="current-password"
                required
              />
            </label>

            {loginError && (
              <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {loginError}
              </p>
            )}

            <button type="submit" className="btn-primary justify-center">
              <LogIn className="h-4 w-4" /> Masuk
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] text-slate-100">
      <header className="border-b border-white/10 bg-[#050505]/90">
        <div className="shell flex min-h-20 items-center justify-between gap-4 py-4">
          <Link to="/" className="inline-flex items-center">
            <img src={ASSETS.logo} alt="Sloto69" className="h-11 w-auto max-w-[170px] object-contain" />
          </Link>
          <button type="button" onClick={handleLogout} className="btn-secondary !px-4 !py-2 text-sm">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </header>

      <section className="shell py-10">
        <div className="mb-8">
          <p className="gold-pill">Admin</p>
          <h1 className="mt-4 font-display text-3xl font-semibold">Submit Artikel</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
            Artikel yang disimpan dari sini akan masuk ke GitHub, memicu redeploy Vercel, lalu bisa dibaca dan di-index sebagai halaman publik.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <form onSubmit={handleSubmit} className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <div className="mb-6 rounded-lg border border-[#10B981]/25 bg-[#10B981]/5 p-4">
              <div className="flex items-center gap-2 text-[#D4AF37]">
                <Sparkles className="h-4 w-4" />
                <h2 className="font-display text-lg font-semibold text-white">Generate dengan OpenAI</h2>
              </div>
              <label className="mt-4 grid gap-2 text-sm font-medium text-slate-200">
                Deskripsi Artikel
                <textarea
                  value={generationDescription}
                  onChange={(event) => setGenerationDescription(event.target.value)}
                  placeholder="Contoh: Buat artikel tentang cara login Sloto69 yang aman untuk pengguna baru, lengkap dengan tips menghindari link palsu."
                  className="min-h-28 rounded-md border border-white/10 bg-black/20 px-3 py-3 text-white outline-none transition focus:border-[#10B981]"
                />
              </label>
              {generationError && (
                <p className="mt-3 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                  {generationError}
                </p>
              )}
              {generationNotice && (
                <p className="mt-3 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-sm text-[#F5E7AD]">
                  {generationNotice}
                </p>
              )}
              <button
                type="button"
                onClick={handleGenerateArticle}
                disabled={isGenerating}
                className="btn-primary mt-4 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Sparkles className="h-4 w-4" />
                {isGenerating ? "Sedang generate..." : "Generate Artikel + Gambar"}
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-medium text-slate-200 md:col-span-2">
                Judul
                <input
                  value={article.title}
                  onChange={(event) => updateArticle("title", event.target.value)}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                  required
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200">
                Slug URL
                <input
                  value={article.slug}
                  onChange={(event) => updateArticle("slug", event.target.value)}
                  placeholder={generatedSlug}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200">
                Kategori
                <input
                  value={article.category}
                  onChange={(event) => updateArticle("category", event.target.value)}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200">
                Tanggal
                <input
                  type="date"
                  value={article.publishedAt}
                  onChange={(event) => updateArticle("publishedAt", event.target.value)}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                  required
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200">
                Waktu Baca
                <input
                  value={article.readTime}
                  onChange={(event) => updateArticle("readTime", event.target.value)}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200 md:col-span-2">
                Ringkasan
                <textarea
                  value={article.excerpt}
                  onChange={(event) => updateArticle("excerpt", event.target.value)}
                  className="min-h-24 rounded-md border border-white/10 bg-black/20 px-3 py-3 text-white outline-none transition focus:border-[#10B981]"
                  required
                />
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200 md:col-span-2">
                Keywords SEO
                <input
                  value={article.keywords}
                  onChange={(event) => updateArticle("keywords", event.target.value)}
                  className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                />
              </label>

              <div className="rounded-lg border border-white/10 bg-black/20 p-4 text-sm text-slate-300 md:col-span-2">
                <h2 className="font-display text-lg font-semibold text-white">Preview SEO</h2>
                <dl className="mt-3 grid gap-2 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">Judul</dt>
                    <dd className="mt-1">{article.title.length} karakter</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">Deskripsi</dt>
                    <dd className="mt-1">{article.excerpt.length} karakter</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">URL</dt>
                    <dd className="mt-1 break-all">/artikel/{generatedSlug || "slug-artikel"}</dd>
                  </div>
                </dl>
                <p className="mt-3 text-xs text-slate-500">
                  Target umum: judul 45-65 karakter, ringkasan 120-160 karakter, slug pendek dan jelas.
                </p>
              </div>

              <label className="grid gap-2 text-sm font-medium text-slate-200 md:col-span-2">
                Gambar Artikel
                <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                  <div className="flex aspect-[16/10] items-center justify-center overflow-hidden rounded-md border border-white/10 bg-black/20">
                    {article.imageUrl ? (
                      <img
                        src={article.imageUrl}
                        alt="Preview gambar artikel"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImagePlus className="h-8 w-8 text-slate-600" />
                    )}
                  </div>
                  <input
                    value={article.imageUrl}
                    onChange={(event) => updateArticle("imageUrl", event.target.value)}
                    placeholder="URL gambar atau hasil generate OpenAI akan muncul di sini"
                    className="h-11 rounded-md border border-white/10 bg-black/20 px-3 text-white outline-none transition focus:border-[#10B981]"
                  />
                </div>
              </label>

              <label className="grid gap-2 text-sm font-medium text-slate-200 md:col-span-2">
                Isi Artikel HTML
                <textarea
                  value={article.body}
                  onChange={(event) => updateArticle("body", event.target.value)}
                  className="min-h-72 rounded-md border border-white/10 bg-black/20 px-3 py-3 font-mono text-sm text-white outline-none transition focus:border-[#10B981]"
                  required
                />
              </label>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> {isSaving ? "Menyimpan..." : "Simpan Artikel"}
              </button>
              {savedArticle && (
                <Link to={`/artikel/${savedArticle.slug}`} className="btn-secondary">
                  <Eye className="h-4 w-4" /> Lihat Artikel
                </Link>
              )}
            </div>
            {saveError && (
              <p className="mt-4 rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {saveError}
              </p>
            )}
            {saveNotice && (
              <p className="mt-4 rounded-md border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-2 text-sm text-emerald-100">
                {saveNotice}
              </p>
            )}
          </form>

          <aside className="rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <h2 className="font-display text-xl font-semibold">Artikel Admin</h2>
            <div className="mt-4 grid gap-3">
              {storedArticles.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada artikel dari admin.</p>
              ) : (
                storedArticles.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/artikel/${item.slug}`}
                    className="rounded-md border border-white/10 bg-black/20 p-3 transition hover:border-[#10B981]/60"
                  >
                    <span className="block text-sm font-semibold text-white">{item.title}</span>
                    <span className="mt-1 block text-xs text-slate-500">{item.publishedAt}</span>
                  </Link>
                ))
              )}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
